/**
 * "What Needs Me" predicate — WHAT-NEEDS-ME-VIEW-1.
 *
 * Filters the task list to items where (a) a human gate is active and
 * (b) the current viewer is the right human. Aligned with the
 * AGENT-MANAGED-WORKING-BOARD-V1 spec §6 (Karpathy rule policy) and the
 * 2026-05-15 substrate-dogfooding audit Gap #3.
 *
 * Inputs:
 *  - `task` — a TaskItem from the engine API
 *  - `ctx`  — viewer identity (handle + owner flag)
 *
 * The functions are kept pure and side-effect-free so the filter is easy
 * to unit-test and easy to reuse outside the board (e.g. for a future
 * sidebar widget or a notification preview).
 */

import type { TaskItem } from '@/api/types';

/** The lifecycle tag that signals a task is parked for human review. */
export const HUMAN_REVIEW_WORKFLOW_TAG = 'workflow:human-review';

/**
 * Meaning-gate tags from AGENT-MANAGED-WORKING-BOARD-V1 §5: when present,
 * the task is asking a human to make a meaning-bearing call (legal, money,
 * trust, product direction, partner commitment).
 */
export const MEANING_GATE_TAGS: ReadonlySet<string> = new Set([
  'decision-required',
  'legal-review',
  'security-review',
  'money-review',
  'partner-review',
  'product-review',
]);

const APPROVER_TAG_PREFIX = 'approver:';

export interface NeedsMeContext {
  /** Display handle, e.g. "geocharris". Matched against `approver:<handle>` tags. */
  meHandle: string | null;
  /** True when the viewer is workspace owner / Director — they see every needs-me task. */
  meIsOwner: boolean;
}

/**
 * True when the task carries any signal that a human is needed:
 *   - `workflow:human-review` lifecycle tag, OR
 *   - `requires_approval: true` field, OR
 *   - one of the meaning-gate tags (decision-required, legal-review, etc.)
 *
 * This is the "is anyone needed?" check, independent of who the viewer is.
 */
export function hasHumanReviewSignal(task: TaskItem): boolean {
  if (task.requires_approval === true) return true;
  const tags = task.tags ?? [];
  for (const t of tags) {
    if (t === HUMAN_REVIEW_WORKFLOW_TAG) return true;
    if (MEANING_GATE_TAGS.has(t)) return true;
  }
  return false;
}

/**
 * True when the viewer is the right human for this task:
 *   - workspace owner / Director sees every needs-me task, OR
 *   - viewer's handle matches an `approver:<handle>` tag (case-insensitive).
 *
 * If the viewer has no handle and isn't the owner, no task matches.
 */
export function matchesViewer(task: TaskItem, ctx: NeedsMeContext): boolean {
  if (ctx.meIsOwner) return true;
  const handle = ctx.meHandle?.trim().toLowerCase();
  if (!handle) return false;
  const target = `${APPROVER_TAG_PREFIX}${handle}`;
  const tags = task.tags ?? [];
  for (const t of tags) {
    if (t.toLowerCase() === target) return true;
  }
  return false;
}

/**
 * The full "needs me right now" predicate: a human is needed AND that
 * human is the viewer.
 *
 * Cancelled and done tasks are excluded — the board's default filter
 * already hides them, but we exclude them here too so the badge count
 * never surfaces resolved work.
 */
export function isNeedsMe(task: TaskItem, ctx: NeedsMeContext): boolean {
  if (task.status === 'cancelled' || task.status === 'done') return false;
  if (!hasHumanReviewSignal(task)) return false;
  return matchesViewer(task, ctx);
}

/**
 * Count of tasks in `tasks` where `isNeedsMe(task, ctx)` is true.
 *
 * Surfaces in the board header as a badge. Updates whenever the task list
 * or viewer identity changes — a transition (agent moves a task into
 * `workflow:human-review`) bumps the count without a manual refresh.
 */
export function countNeedsMe(tasks: ReadonlyArray<TaskItem>, ctx: NeedsMeContext): number {
  let n = 0;
  for (const t of tasks) {
    if (isNeedsMe(t, ctx)) n++;
  }
  return n;
}
